import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Clock, Coins } from "lucide-react";
import { SOINS } from "../data/content";
import { Reveal } from "../components/Reveal";

export default function SoinDetail() {
  const { slug } = useParams();
  const soin = SOINS.find((s) => s.slug === slug);
  if (!soin) return <Navigate to="/soins" replace />;

  const related = SOINS.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`soin-detail-${soin.slug}`}>
      <section className="pt-32 lg:pt-40 pb-14 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <Link to="/soins" data-testid="soin-back-link" className="inline-flex items-center gap-2 text-sm text-sage hover:text-olive transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Tous les soins
          </Link>
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-olive mb-5">{soin.tag}</p>
              <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-ink leading-[1.05]">{soin.name}</h1>
              <p className="mt-7 font-serif text-2xl font-light text-sage leading-relaxed max-w-xl">{soin.intro}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span data-testid="soin-price" className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-olive-soft px-4 py-2 text-sm text-olive-dark">
                  <Coins className="w-4 h-4" strokeWidth={1.5} /> {soin.price}
                </span>
                <span data-testid="soin-duration" className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-olive-soft px-4 py-2 text-sm text-olive-dark">
                  <Clock className="w-4 h-4" strokeWidth={1.5} /> {soin.duration}
                </span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-olive-border shadow-[0_25px_50px_-25px_rgba(49,63,21,0.35)]">
                <img src={soin.image} alt={soin.name} data-testid="soin-image" className="w-full h-80 sm:h-96 object-cover" />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="py-16 lg:py-20 px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7 space-y-6" data-testid="soin-description">
          {soin.description.map((p, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <p className="text-sage leading-relaxed text-base sm:text-lg">{p}</p>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="bg-beige-50 border border-olive-border rounded-3xl p-8" data-testid="soin-bienfaits">
              <p className="text-xs uppercase tracking-[0.25em] text-olive mb-6">Les bienfaits</p>
              <ul className="space-y-4">
                {soin.bienfaits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ink">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-olive" strokeWidth={2} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/contact?soin=${soin.slug}`}
                data-testid="soin-booking-button"
                className="mt-8 flex items-center justify-center gap-2 w-full rounded-full bg-olive px-6 py-4 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5"
              >
                Je réserve ce soin <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-beige-200/50 border-t border-olive-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-olive mb-3">Poursuivre la découverte</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-ink">D'autres soins à explorer</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-8">
            {related.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.1}>
                <Link to={`/soins/${s.slug}`} data-testid={`related-soin-${s.slug}`} className="group block">
                  <div className="overflow-hidden rounded-2xl border border-olive-border">
                    <img src={s.image} alt={s.name} loading="lazy" className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-4 font-serif text-xl text-ink group-hover:text-olive transition-colors">{s.name}</h3>
                  <p className="text-xs text-stone2 mt-1">{s.price} · {s.duration}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
